import { createSearchParams, useNavigate } from "react-router-dom";
import { useContext } from "react";
import { Trans } from "@lingui/react/macro";
import { styled } from "styled-components";
import {
  IconButton,
  Button as MirandaButton,
  ScrollableContainer,
  Spinner,
  Text,
} from "@fidelix/fx-miranda";
import {
  Button,
  Dialog,
  DialogTrigger,
  OverlayTriggerStateContext,
  Popover,
} from "react-aria-components";
import { useNavigatonPath } from "~utils/url";
import {
  deviceNoticationLimit,
  deviceNotificationListLimit,
} from "../utils/consts";
import { useDevicesQuery } from "./device.queries";
import { DeviceLite, DeviceStatus } from "./device.model";
import { useDeviceStateContext } from "./device-status-context";

export const disconnectedDeviceStatuses = [
  DeviceStatus.AuthFailure,
  DeviceStatus.Disconnected,
  DeviceStatus.VersionFailure,
];

function DevicesMenu({
  devices,
  isFetching,
  isLoading,
  isError,
}: {
  devices: DeviceLite[];
  isFetching: boolean;
  isLoading: boolean;
  isError: boolean;
}) {
  const navigate = useNavigate();
  const { to } = useNavigatonPath();
  const state = useContext(OverlayTriggerStateContext);

  if (!state) return null;

  return (
    <DevicesPopover>
      <DevicesMenuHeader>
        <Text variant="subtitle1">
          <Trans>Device disconnections ({devices.length})</Trans>
        </Text>
        {isFetching && !isLoading && <Spinner />}
      </DevicesMenuHeader>
      <DevicesMenuBody>
        {devices.length > 0 ? (
          devices.map((d) => (
            <DeviceButton key={d.id}>
              <Text variant="body">
                <Trans>Disconnection</Trans>
              </Text>
              <Text variant="bodyLight">{d.name}</Text>
              {d.building && <Text variant="bodySmallLight">{d.building}</Text>}
            </DeviceButton>
          ))
        ) : isLoading ? (
          <EmptyContainer>
            <Spinner size="medium" />
          </EmptyContainer>
        ) : isError ? (
          <EmptyContainer>
            <Text variant="body">
              <Trans>Failed to load devices</Trans>
            </Text>
          </EmptyContainer>
        ) : (
          <EmptyContainer>
            <Text variant="body">
              <Trans>No device disconnections</Trans>
            </Text>
          </EmptyContainer>
        )}
      </DevicesMenuBody>
      <DevicesMenuFooter>
        <MirandaButton
          variant="text"
          icon="chevronRight"
          iconPlacement="right"
          onPress={() => {
            state.close();
            navigate(
              to(
                `devices?${createSearchParams({ status: `[${disconnectedDeviceStatuses}]` }).toString()}`,
              ),
            );
          }}
        >
          <Text variant="bodyBold" color="darkPrimary">
            <Trans>Show all affected devices</Trans>
          </Text>
        </MirandaButton>
      </DevicesMenuFooter>
    </DevicesPopover>
  );
}

export function DeviceNavBarIcon() {
  const {
    data: devices = [],
    refetch,
    isLoading,
    isFetching,
    isError,
  } = useDevicesQuery(
    {
      page: 1,
      pageSize: deviceNotificationListLimit,
      status: disconnectedDeviceStatuses,
    },
    { enabled: false },
  );
  const { deviceStatusDict } = useDeviceStateContext();
  const onOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      refetch();
    }
  };

  const failedDevices = Object.values(deviceStatusDict).filter((val) =>
    disconnectedDeviceStatuses.includes(val.status),
  );

  return (
    <Container>
      <DialogTrigger onOpenChange={onOpenChange}>
        <IconButton
          icon="connectionWifi"
          size="desktop"
          variant="transparent"
          color="white"
        />
        <Popover>
          <Dialog>
            <DevicesMenu
              devices={devices}
              isFetching={isFetching}
              isLoading={isLoading}
              isError={isError}
            />
          </Dialog>
        </Popover>
        {isLoading ? (
          <SpinnerContainer>
            <Spinner size={24} />
          </SpinnerContainer>
        ) : (
          failedDevices.length > 0 && (
            <NumberContainer>
              <Text variant="bodyExtraSmall" color="white">
                {failedDevices.length >= deviceNoticationLimit
                  ? `${deviceNoticationLimit}+`
                  : failedDevices.length}
              </Text>
            </NumberContainer>
          )
        )}
      </DialogTrigger>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  position: relative;
`;

const SmallInfoContainer = styled.div`
  aspect-ratio: 1 / 1;
  min-width: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2px;
  pointer-events: none;
`;

const NumberContainer = styled(SmallInfoContainer)`
  width: 20px;
  background: ${(p) => p.theme.colors.error};
  border-radius: 50%;
  border: 1px solid ${(p) => p.theme.colors.white};
  color: ${(p) => p.theme.colors.white};
  position: absolute;
  right: -5px;
  top: -5px;
  ${(p) => p.theme.media.phone} {
    right: -10px;
    top: -10px;
  }
`;

const SpinnerContainer = styled(SmallInfoContainer)`
  position: absolute;
  color: ${(p) => p.theme.colors.lightNeutral};
  right: -5px;
  top: -5px;
  ${(p) => p.theme.media.phone} {
    right: -10px;
    top: -10px;
  }
`;

const DevicesPopover = styled.div`
  width: 420px;
  position: absolute;
  background: ${(p) => p.theme.colors.white};
  border: 1px solid ${(p) => p.theme.colors.lightNeutral};
  border-radius: 8px;
  box-shadow: 0px 4px 4px 0px #0000001a;
  transform: translateX(-400px);
  ${(p) => p.theme.media.phone} {
    width: 350px;
    transform: translateX(-250px);
  }
`;

const EmptyContainer = styled.div`
  width: 100%;
  display: flex;
  padding: ${(p) => p.theme.space("xlarge")};
  justify-content: center;
`;

const DeviceButton = styled(Button)`
  width: 100%;
  display: flex;
  padding: ${(p) => p.theme.space("medium")};
  gap: ${(p) => p.theme.space("xxsmall")};
  flex-direction: column;
  &:hover {
    background: ${(p) => p.theme.colors.mutedNeutralBackground};
  }
  &:not(:last-child) {
    border-bottom: 1px solid ${(p) => p.theme.colors.primaryMuted};
  }
`;

const DevicesMenuBody = styled(ScrollableContainer)`
  max-height: 420px;
`;

const DevicesMenuHeader = styled.div`
  padding: ${(p) => p.theme.space("medium")};
  border-bottom: 1px solid ${(p) => p.theme.colors.primaryMuted};
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.space("small")};
`;

const DevicesMenuFooter = styled.div`
  padding: ${(p) => p.theme.space("medium")};
  display: flex;
  justify-content: center;
  align-items: center;
  border-top: 1px solid ${(p) => p.theme.colors.primaryMuted};
`;
